// The billing statement (owner, 2026-09-30 / 10-01): "this machine was used on this day at this time,
// and these are the courts active or not during that time" — a neutral, billing-style A4 document per
// machine and period: every session (paid, voided, free trial WITH its code), the customer's nickname,
// Court 1 / Court 2 during the machine's running time (or "Empty"), the shares, what is already
// settled and the balance due to HighMark. No flags, no side comments. The server builds the figures
// (GET /devices/{id}/billing); this panel picks the period and opens the printable page — the
// browser's Print → "Save as PDF" makes the file.
import { useState } from 'react';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { RED, GREY, labelStyle, inputStyle } from './KioskDialogs';
import { getBillingStatement, errMsg, ymd } from './kioskApi';

const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '14px 16px' };
const TRIAL_STOP = { WEATHER: 'rain', EMERGENCY: 'emergency', MACHINE_FAULT: 'machine problem', KIOSK_FAULT: 'kiosk problem',
  COURT_CLOSED: 'court closed', MISTAKE: 'started by mistake', OUTAGE: 'kiosk problem' };
const TZ = 'Asia/Manila';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const peso = (c) => (Number(c) || 0) === 0 ? '–' : `₱${((Number(c) || 0) / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pesoZero = (c) => `₱${((Number(c) || 0) / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (ms, opts) => new Intl.DateTimeFormat('en-US', { timeZone: TZ, ...opts }).format(new Date(ms));
const dateOf = (ms) => fmt(ms, { month: 'short', day: 'numeric', year: 'numeric' });
const dayOf = (ms) => fmt(ms, { weekday: 'short' });
const timeOf = (ms) => fmt(ms, { hour: 'numeric', minute: '2-digit', hour12: true });
const sameDay = (a, b) => fmt(a, { year: 'numeric', month: '2-digit', day: '2-digit' }) === fmt(b, { year: 'numeric', month: '2-digit', day: '2-digit' });
const longDate = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); };

// Bold "Empty…" stretches inside a court line, exactly as the approved PDF.
function courtCell(c) {
  const html = esc(c.line).replace(/(Empty(?: until [0-9:]+ [AP]M| [0-9:]+ (?:[AP]M )?– [0-9:]+ [AP]M)?)/g, '<span class="empty">$1</span>');
  return `<td${c.has_empty || c.empty ? ' class="has-empty"' : ''}>${html}</td>`;
}

export function buildStatementHtml(s) {
  const courts = s.courts || [];
  const sum = s.summary || {};
  const r = s.rates || {};
  const body = (s.rows || []).map((row, i) => {
    const next = !sameDay(row.started_at_ms, row.ended_at_ms) ? `<div class="sub">${esc(fmt(row.ended_at_ms, { month: 'short', day: 'numeric' }))}</div>` : '';
    // 2026-10-01: a trial is a balance in up to 2 parts — "part 1 · paused: rain" then "part 2 · finished".
    const why = TRIAL_STOP[row.stop_reason] || (row.stop_reason ? String(row.stop_reason).toLowerCase().replace(/_/g, ' ') : '');
    const outcome = !row.trial_outcome ? ''
      : `${row.trial_part ? ` · part ${row.trial_part}` : ''} · ${row.trial_outcome === 'paused' ? `paused${why ? `: ${why}` : ''}` : row.trial_outcome === 'forfeited' ? 'ended by player' : 'finished'}`;
    const vl = row.voucher;
    const payer = !vl ? '' : vl.owner_funded_centavos === 0 ? 'court-paid' : vl.court_funded_centavos === 0 ? 'HighMark-paid' : 'split';
    const voucherSub = !vl ? '' : vl.consumed && vl.discount_centavos > 0
      ? `<div class="sub">Voucher ${esc(vl.code)} · −${pesoZero(vl.discount_centavos)} · ${payer}</div>`
      : vl.consumed ? `<div class="sub">Voucher ${esc(vl.code)}</div>` : `<div class="sub">Voucher ${esc(vl.code)} · given back</div>`;
    const status = row.trial ? `Free trial${row.voucher_code ? `<div class="sub">${esc(row.voucher_code)}${outcome}</div>` : ''}` : `${esc(row.status)}${voucherSub}`;
    const cls = row.status === 'Voided' ? 'voided' : row.trial ? 'trial' : '';
    return `<tr class="${cls}">
<td class="c">${i + 1}</td><td class="nw">${esc(dateOf(row.started_at_ms))}</td><td class="c">${esc(dayOf(row.started_at_ms))}</td>
<td class="c nw">${esc(timeOf(row.started_at_ms))}</td><td class="c nw">${esc(timeOf(row.ended_at_ms))}${next}</td>
<td class="c">${esc(row.minutes_used ?? '')}</td><td>${esc(row.customer_name || '—')}</td>
${courts.map((_, k) => courtCell((row.courts || [])[k] || { line: '—' })).join('')}
<td class="c">${status}</td><td class="r">${peso(row.charged_centavos)}</td>
<td class="r">${row.billed_hours ? Number(row.billed_hours).toFixed(2) : '–'}</td>
<td class="r">${peso(row.owner_share_centavos)}</td><td class="r">${peso(row.court_share_centavos)}</td></tr>`;
  }).join('');
  const courtHeads = courts.map(n => `<th>${esc(n)} during this time</th>`).join('');
  const span = 7 + courts.length;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Ball Machine Usage Statement — ${esc(s.machine_name)}</title>
<style>
@page { size: A4 landscape; margin: 10mm 11mm 10mm; }
* { box-sizing: border-box; }
body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; font-size: 9pt; margin: 16px; background: #fff; }
@media print { body { margin: 0; } .bar { display: none; } }
.bar { display: flex; gap: 8px; margin-bottom: 14px; }
.bar button { font: 700 12px Arial; padding: 9px 14px; border-radius: 8px; border: 1px solid #1F3864; background: #1F3864; color: #fff; cursor: pointer; }
.bar span { font-size: 11px; color: #666; align-self: center; }
.top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #1F3864; padding-bottom: 10px; gap: 20px; }
h1 { margin: 0 0 8px; font-size: 18pt; letter-spacing: .5px; color: #1F3864; }
.meta { display: grid; grid-template-columns: 120px auto; row-gap: 3px; }
.meta b { color: #444; }
.rates { border: 1px solid #c9d1e3; border-radius: 4px; padding: 8px 12px; min-width: 250px; background: #f6f8fc; }
.rates h3 { margin: 0 0 6px; font-size: 9pt; text-transform: uppercase; letter-spacing: .6px; color: #1F3864; }
.rates div { display: flex; justify-content: space-between; padding: 2px 0; gap: 16px; }
table { width: 100%; border-collapse: collapse; margin-top: 14px; }
thead { display: table-header-group; }
th { background: #1F3864; color: #fff; font-size: 8.5pt; padding: 6px 5px; text-align: center; border: 1px solid #1F3864; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
td { border: 1px solid #d0d4dc; padding: 4px 5px; vertical-align: middle; }
tr { page-break-inside: avoid; }
td.c { text-align: center; } td.r { text-align: right; white-space: nowrap; } td.nw { white-space: nowrap; }
td.has-empty { background: #efefef; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.empty { font-weight: 700; color: #555; }
tr.voided td { color: #777; }
.sub { font-size: 7.5pt; color: #777; }
tr.total td { background: #dfe5f1; font-weight: 700; border-top: 2px solid #1F3864; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.bottom { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 16px; page-break-inside: avoid; gap: 20px; }
.note { font-size: 8pt; color: #666; max-width: 520px; line-height: 1.45; }
.summary { width: 340px; border-collapse: collapse; margin: 0; }
.summary td { border: none; border-bottom: 1px solid #e1e4ea; padding: 4px 6px; }
.summary .h td { font-weight: 700; color: #1F3864; text-transform: uppercase; font-size: 8.5pt; letter-spacing: .6px; border-bottom: 2px solid #1F3864; }
.summary .due td { font-weight: 700; font-size: 11pt; background: #dfe5f1; border-bottom: 3px double #1F3864; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>
<div class="bar"><button onclick="window.print()">Print / Save as PDF</button><span>Tip: choose “Save as PDF” as the printer, A4 landscape.</span></div>
<div class="top"><div>
<h1>BALL MACHINE USAGE STATEMENT</h1>
<div class="meta">
<b>Machine owner</b><span>HighMark</span>
<b>Site</b><span>${esc(s.court_name || s.site_name || '—')}</span>
<b>Machine</b><span>${esc(s.machine_name)}</span>
<b>Statement period</b><span>${esc(longDate(s.period.from))} – ${esc(longDate(s.period.to))}${s.period.kind === 'since_paid' ? ' (not yet settled)' : ''}</span>
<b>Statement date</b><span>${esc(longDate(s.statement_date))}</span>
</div></div>
<div class="rates"><h3>Rates</h3>
<div><span>Machine rate per hour</span><b>${pesoZero(r.rate_centavos_per_hour)}</b></div>
<div><span>HighMark share per hour</span><b>${pesoZero(r.owner_centavos_per_hour)}</b></div>
<div><span>Court share per hour</span><b>${pesoZero(r.court_centavos_per_hour)}</b></div>
</div></div>
<table><thead><tr><th>#</th><th>Date</th><th>Day</th><th>Start</th><th>End</th><th>Minutes Used</th><th>Nickname</th>${courtHeads}
<th>Status</th><th>Amount</th><th>Billed Hours</th><th>HighMark Share</th><th>Court Share</th></tr></thead>
<tbody>${body || `<tr><td colspan="${span + 6}" class="c">No machine sessions in this period.</td></tr>`}
<tr class="total"><td colspan="5">TOTAL</td><td class="c">${(s.rows || []).reduce((a, x) => a + (Number(x.minutes_used) || 0), 0)}</td><td colspan="${1 + courts.length + 1}"></td>
<td class="r">${pesoZero(sum.gross_centavos)}</td><td class="r">${Number(sum.billed_hours || 0).toFixed(2)}</td>
<td class="r">${pesoZero(sum.owner_centavos)}</td><td class="r">${pesoZero(sum.court_centavos)}</td></tr></tbody></table>
<div class="bottom">
<div class="note">Times are Manila time. The court columns show the court bookings during the machine's running time; <b>Empty</b> means no booking on that court for that period. Voided sessions carry no charge. A free trial is ₱0 for everyone; its code is shown under the status.</div>
<table class="summary">
<tr class="h"><td colspan="2">Summary</td></tr>
<tr><td>Machine sessions</td><td class="r">${sum.sessions || 0}</td></tr>
<tr><td>Charged sessions</td><td class="r">${sum.charged || 0}</td></tr>
<tr><td>Voided sessions (no charge)</td><td class="r">${sum.voided || 0}</td></tr>
<tr><td>Free trials</td><td class="r">${sum.trials || 0}${sum.trials ? ` · ${sum.trial_minutes} min` : ''}</td></tr>
${sum.discount_count ? `<tr><td>Voucher discounts (${sum.discount_count})<div class="sub">court-paid ${pesoZero(sum.discount_court_funded_centavos)} · HighMark-paid ${pesoZero(sum.discount_owner_funded_centavos)}</div></td><td class="r">−${pesoZero(sum.discount_centavos)}</td></tr>` : ''}
<tr><td>Billed hours</td><td class="r">${Number(sum.billed_hours || 0).toFixed(2)}</td></tr>
<tr><td>Gross machine sales</td><td class="r">${pesoZero(sum.gross_centavos)}</td></tr>
<tr><td>Court share</td><td class="r">${pesoZero(sum.court_centavos)}</td></tr>
<tr><td><b>HighMark share</b></td><td class="r"><b>${pesoZero(sum.owner_centavos)}</b></td></tr>
<tr><td>Less: already settled</td><td class="r">${pesoZero(sum.settled_owner_centavos)}</td></tr>
<tr class="due"><td>BALANCE DUE TO HIGHMARK</td><td class="r">${pesoZero(sum.balance_due_centavos)}</td></tr>
</table></div></body></html>`;
}

const monthStart = (d = new Date()) => ymd(new Date(d.getFullYear(), d.getMonth(), 1));
const PERIODS = [
  ['since_paid', 'Since last settlement'],
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['custom', 'Custom dates'],
];

export function BillingPanel({ kioskId }) {
  const [kind, setKind] = useState('since_paid');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(ymd());
  const [busy, setBusy] = useState(false);

  const params = () => {
    if (kind === 'since_paid') return { period: 'since_paid' };
    if (kind === 'this_month') return { period: 'range', from: monthStart(), to: ymd() };
    if (kind === 'last_month') {
      const n = new Date();
      return { period: 'range', from: ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), to: ymd(new Date(n.getFullYear(), n.getMonth(), 0)) };
    }
    return { period: 'range', from, to };
  };

  const generate = async () => {
    const win = window.open('', '_blank');          // opened inside the click so no popup blocker stops it
    if (!win) { toast.error('Allow pop-ups for this site to open the statement.'); return; }
    win.document.write('<p style="font-family:Arial;padding:20px">Building the statement…</p>');
    setBusy(true);
    try {
      const s = await getBillingStatement(kioskId, params());
      win.document.open();
      win.document.write(buildStatementHtml(s));
      win.document.close();
    } catch (e) {
      win.close();
      toast.error(errMsg(e, 'Could not build the statement'));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ ...card, marginBottom: 16 }} data-testid="kiosk-billing-panel">
      <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Billing statement</h2>
      <p style={{ fontSize: 12, color: GREY, marginTop: 2, maxWidth: 620, lineHeight: 1.5 }}>
        Every session in the period — paid, voided and free trials with their code — with the customer&apos;s nickname, the courts that were booked or empty during it, the shares and the balance due. Opens as a page you can print or save as PDF.
      </p>
      <div className="flex gap-2 flex-wrap mt-3 items-end">
        {PERIODS.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setKind(id)} data-testid={`kiosk-billing-period-${id}`}
            style={{ padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${kind === id ? RED : '#2A2A2A'}`, background: kind === id ? RED + '18' : 'transparent', color: kind === id ? RED : '#a3a3a3' }}>{label}</button>
        ))}
        {kind === 'custom' && (
          <>
            <label><span style={labelStyle}>From</span><input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="kiosk-billing-from" style={{ ...inputStyle, marginTop: 4, width: 160 }} /></label>
            <label><span style={labelStyle}>To</span><input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="kiosk-billing-to" style={{ ...inputStyle, marginTop: 4, width: 160 }} /></label>
          </>
        )}
        <button type="button" onClick={generate} disabled={busy || (kind === 'custom' && (!from || !to || to < from))} data-testid="kiosk-billing-generate"
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px',
            fontSize: 13, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer', marginLeft: 'auto' }}>
          <FileText className="h-4 w-4" /> {busy ? 'Building…' : 'Generate statement'}
        </button>
      </div>
    </div>
  );
}
